import { Link } from "react-router-dom";
import {
  ArrowRight, Building2, Store, ArrowLeftRight, TrendingUp, Users, ShoppingCart,
  Globe, Smartphone, Languages, Sparkles, Check, Zap, BarChart3, Wallet,
  Package, FileText, Briefcase, ChevronRight,
} from "lucide-react";
import Testimonials from "../components/Testimonials";
import CTASection from "../components/CTASection";

const FEATURES = [
  { icon: <Building2 size={22} />, title: "Multi-Warehouse", desc: "Manage stock across unlimited warehouses and transfer between locations with a few clicks." },
  { icon: <Store size={22} />, title: "Point of Sale", desc: "Built-in POS with barcode scanning, receipts, and multiple payment methods." },
  { icon: <ArrowLeftRight size={22} />, title: "Purchase & Sales", desc: "Handle purchases, sales, returns, and quotations with full document lifecycle tracking." },
  { icon: <TrendingUp size={22} />, title: "Real-Time Reports", desc: "Dashboards and detailed reports for stock levels, profit, top products, and more." },
  { icon: <Users size={22} />, title: "Profit & Loss", desc: "Track the daily, weekly, monthly and yearly progress of your business." },
  { icon: <ShoppingCart size={22} />, title: "WooCommerce", desc: "Sync your products, orders, and stock levels with WooCommerce for seamless online selling." },
  { icon: <Globe size={22} />, title: "Online Store", desc: "Launch your own branded online store and let customers browse and order directly." },
  { icon: <Smartphone size={22} />, title: "Client Portal", desc: "Give clients a self-service portal to view invoices, track orders, and manage their account." },
  { icon: <Languages size={22} />, title: "24+ Languages", desc: "Reach a global audience with built-in support for over 24 languages out of the box." },
];

const STEPS = [
  { num: "01", title: "Sign up in 60 seconds", desc: "Create your Brivo360 account, no credit card required, and onboard your business in minutes." },
  { num: "02", title: "Import your products", desc: "Upload your inventory via CSV, scan barcodes, or sync from WooCommerce — your choice." },
  { num: "03", title: "Start selling", desc: "Run your storefront, accept payments, manage orders, and watch your numbers grow." },
];

const SERVES = [
  { tag: "Solopreneurs", title: "Just you, building big.", desc: "Sell from your phone, accept payments anywhere, and stay organized.", href: "/serve/solopreneurs", accent: "bg-[#FFE4E8]" },
  { tag: "Medium Businesses", title: "Scaling without chaos.", desc: "Multi-staff, multi-location, multi-warehouse — one calm dashboard.", href: "/serve/medium", accent: "bg-[#FFF9E6]" },
  { tag: "Large Businesses", title: "Run a complex operation, simply.", desc: "Advanced analytics, custom roles, deep integrations.", href: "/serve/large", accent: "bg-[#E8F5E9]" },
  { tag: "Enterprise", title: "Custom-built to your scale.", desc: "Dedicated CSM, SLAs, SSO, on-premise options.", href: "/serve/enterprise", accent: "bg-[#12b571] text-white" },
];

const MODULES = [
  { icon: <Package size={20} />, name: "Inventory" },
  { icon: <ShoppingCart size={20} />, name: "Orders" },
  { icon: <Wallet size={20} />, name: "Payments" },
  { icon: <FileText size={20} />, name: "Invoicing" },
  { icon: <BarChart3 size={20} />, name: "Analytics" },
  { icon: <Users size={20} />, name: "Customers" },
  { icon: <Briefcase size={20} />, name: "Staff" },
  { icon: <Globe size={20} />, name: "Storefront" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden hero-gradient grain">
        <div className="absolute top-20 -left-32 w-96 h-96 rounded-full bg-[#FFE4E8] opacity-50 blur-3xl animate-blob" />
        <div className="absolute bottom-0 -right-32 w-96 h-96 rounded-full bg-[#E8F5E9] opacity-60 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-strong border border-[#12b571]/10 text-black text-xs font-semibold mb-7 animate-fade-up">
              <Sparkles size={14} className="text-black" />
              The all-in-one platform for modern businesses
            </div>
            <h1 data-testid="hero-headline" className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.02] animate-fade-up delay-100">
              Run your business<br />
              <span className="italic text-black/70 font-medium">without the chaos.</span>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-black/75 max-w-2xl mx-auto leading-relaxed animate-fade-up delay-200">
              Inventory, payments, storefront, analytics — Brivo360 gives you one calm dashboard
              to sell smarter, manage stock effortlessly, and get paid faster. Anywhere.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up delay-300">
              <Link to="/register" data-testid="hero-cta-primary" className="btn-primary text-base">
                Start free trial <ArrowRight size={18} />
              </Link>
              <Link to="/pricing" data-testid="hero-cta-secondary" className="btn-secondary text-base">
                See pricing
              </Link>
            </div>
            <p className="mt-5 text-sm text-black/50 animate-fade-up delay-400">
              14-day free trial • No credit card • Cancel anytime
            </p>
          </div>

          {/* Dashboard preview */}
          <div className="mt-20 animate-fade-up delay-500">
            <div className="relative max-w-6xl mx-auto">
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#FFE4E8] via-[#FFF9E6] to-[#E8F5E9] rounded-[32px] blur-2xl opacity-60" />
              <div className="relative bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(27,67,50,0.25)] border border-[#12b571]/8 overflow-hidden">
                <div className="flex items-center gap-1.5 px-5 py-3.5 border-b border-[#12b571]/8 bg-[#FFFCF2]">
                  <div className="w-3 h-3 rounded-full bg-[#FFB4B4]" />
                  <div className="w-3 h-3 rounded-full bg-[#FFE4A8]" />
                  <div className="w-3 h-3 rounded-full bg-[#B4E4B4]" />
                  <div className="ml-4 px-3 py-1 rounded-md bg-white/70 text-xs text-black/60 font-mono">bright.brivo360.com</div>
                </div>

                <img 
            src="https://brivo360.com/images/home11.png" 
           
          />



                {/* <div className="grid md:grid-cols-12 min-h-[420px]">
                  <aside className="md:col-span-3 bg-[#FFFCF2] border-r border-[#12b571]/8 p-6">
                    <div className="text-xs text-black/50 font-semibold mb-3">QUICK ACCESS</div>
                    <div className="space-y-1">
                      {["Dashboard", "Orders", "Products", "Customers", "Analytics", "Marketing"].map((l, i) => (
                        <div key={l} className={`px-3 py-2 rounded-lg text-sm ${i === 0 ? "bg-[#12b571] text-white font-semibold" : "text-black/70"}`}>
                          {l}
                        </div>
                      ))}
                    </div>
                  </aside>
                  <div className="md:col-span-9 p-7">
                    <h3 className="font-display font-bold text-2xl text-black">Hi Gospel!</h3>
                    <p className="text-sm text-black/60 mt-1">Here's how your business is doing today</p>
                    <div className="grid grid-cols-4 gap-3 mt-5">
                      {[
                        { v: "1,240", l: "Orders", c: "bg-[#E8F5E9]" },
                        { v: "289", l: "Products sold", c: "bg-[#E0F0FF]" },
                        { v: "136", l: "New Customers", c: "bg-[#FFF3D6]" },
                        { v: "2,532", l: "Website Visits", c: "bg-[#FFE4E8]" },
                      ].map((s) => (
                        <div key={s.l} className={`${s.c} rounded-xl p-4`}>
                          <div className="text-2xl font-display font-extrabold text-black">{s.v}</div>
                          <div className="text-xs text-black/60 mt-1">{s.l}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 grid grid-cols-4 gap-3">
                      {[
                        { l: "Total Sales", v: "₦12.4M" },
                        { l: "Total Settled", v: "₦10.0M" },
                        { l: "Total Owed", v: "₦0.0" },
                        { l: "Offline Sales", v: "₦12.4M" },
                      ].map((s) => (
                        <div key={s.l} className="border-t border-[#12b571]/10 pt-3">
                          <div className="text-xs text-black/50">{s.l}</div>
                          <div className="font-display font-bold text-lg text-black">{s.v}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 flex items-end gap-2 h-32">
                      {[40, 95, 35, 55, 85, 70, 90, 45, 65, 35, 25, 50].map((h, i) => (
                        <div key={i} className="flex-1 flex flex-col gap-1">
                          <div className="rounded-t-md bg-[#12b571]" style={{ height: `${h}%` }} />
                          <div className="rounded-b-md bg-[#52B788]" style={{ height: `${h * 0.6}%` }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>,, */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section data-testid="social-proof" className="py-16 bg-white border-y border-[#12b571]/8">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-center text-sm uppercase tracking-widest text-black/50 font-semibold mb-10">
            Trusted by 12,000+ growing businesses
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center opacity-70">
            {["Empire", "Pandora", "MGL", "Kova", "Ultikel", "MTN"].map((b) => (
              <div key={b} className="text-center font-display font-bold text-xl text-black/60 hover:text-black transition-colors">
                {b}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES GRID 9 cards */}
      <section data-testid="features-grid" className="py-24 md:py-32 bg-[#FFFCF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFE4E8] text-black text-xs font-semibold mb-4">
              Core features
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight leading-[1.05]">
              One platform. Every tool<br />you'll ever need.
            </h2>
            <p className="mt-5 text-lg text-black/70 max-w-xl">
              From the storefront to the warehouse, Brivo360 unifies the messy parts of running a business into one beautiful workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="bg-white rounded-3xl p-7 border border-[#12b571]/8 card-hover relative overflow-hidden group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FFF9E6] flex items-center justify-center text-black mb-6 group-hover:bg-[#12b571] group-hover:text-white transition-colors">
                  {f.icon}
                </div>
                <h3 className="font-display font-bold text-xl text-black mb-2">{f.title}</h3>
                <p className="text-black/70 leading-relaxed text-sm">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE SPLIT 1 — Have visibility on your business operations */}
      <section data-testid="feature-split-1" className="py-24 md:py-32 bg-[#E8F5E9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-3 py-1 rounded-full bg-white/70 text-black text-xs font-semibold mb-4">
              Operations
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight leading-[1.05]">
              Have visibility on your business operations
            </h2>
            <p className="mt-6 text-lg text-black/75 leading-relaxed">
              Don't do business blindly. View your staff actions, sales, inventory & analytics across numerous store locations with one dashboard.
            </p>
            <ul className="mt-7 space-y-3">
              {["Track inventory in real-time across locations", "Monitor staff performance and permissions", "Centralized analytics from one dashboard"].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Check size={20} className="text-black mt-0.5" />
                  <span className="text-black/85">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-9">
              <Link to="/business/manage-operations" className="text-black font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                Learn more <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-square max-w-md mx-auto relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#52B788]/30 to-[#12b571]/10 animate-blob" />
              <img
                src="https://brivo360.com/images/store-lady.jpg"
                alt="Business operations"
                className="absolute inset-8 rounded-full object-cover border-8 border-white shadow-xl"
              />
              <div className="absolute top-12 -left-4 bg-white rounded-2xl px-4 py-3 shadow-lg flex items-center gap-2 animate-float">
                <div className="w-8 h-8 rounded-lg bg-[#FFF9E6] flex items-center justify-center"><Users size={16} className="text-black" /></div>
                <span className="font-semibold text-sm text-black">Multiple Locations</span>
              </div>
              <div className="absolute bottom-20 -right-2 bg-white rounded-2xl px-4 py-3 shadow-lg flex items-center gap-2 animate-float" style={{ animationDelay: "1.5s" }}>
                <div className="w-8 h-8 rounded-lg bg-[#FFE4E8] flex items-center justify-center"><Briefcase size={16} className="text-black" /></div>
                <span className="font-semibold text-sm text-black">Staff Accounts</span>
              </div>
              <div className="absolute -bottom-4 left-12 bg-white rounded-2xl px-4 py-3 shadow-lg flex items-center gap-2 animate-float" style={{ animationDelay: "3s" }}>
                <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center"><Package size={16} className="text-black" /></div>
                <span className="font-semibold text-sm text-black">Manage Inventories</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE SPLIT 2 — Save Customer Details */}
      <section data-testid="feature-split-2" className="py-24 md:py-32 bg-[#FFE4E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="lg:order-2">
            <span className="inline-block px-3 py-1 rounded-full bg-white/70 text-black text-xs font-semibold mb-4">
              Customer CRM
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight leading-[1.05]">
              Save Customer Details & Keep Them Loyal on Brivo360.
            </h2>
            <p className="mt-6 text-lg text-black/75 leading-relaxed">
              Record customer details with their purchase history, shipping address and contact details for marketing purposes — and turn one-time buyers into repeat fans.
            </p>
            <ul className="mt-7 space-y-3">
              {["Auto-saved customer profiles & purchase history", "Email & SMS marketing built in", "Loyalty rewards and discount codes"].map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Check size={20} className="text-black mt-0.5" />
                  <span className="text-black/85">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-9">
              <Link to="/business/sell-online" className="text-black font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                Learn more <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          <div className="lg:order-1 flex justify-center">
            <div className="relative w-full max-w-sm">
              <div className="absolute -inset-8 bg-white/40 rounded-[40px] blur-2xl" />
              <div className="relative bg-white rounded-[32px] shadow-2xl border-8 border-white p-2 overflow-hidden">
                <div className="bg-[#FFFCF2] rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-5">
                    <div className="text-xs font-semibold text-[#52B788]">Back</div>
                    <div className="text-sm font-semibold text-black">Akinbola Chinedu</div>
                    <div className="text-xs font-semibold text-[#52B788]">Edit</div>
                  </div>
                  <div className="flex gap-2 text-xs mb-4">
                    <div className="flex-1 text-center py-2 bg-white rounded-lg border border-[#12b571]/10 font-semibold text-black">Customer info</div>
                    <div className="flex-1 text-center py-2 text-black/60">Recent Purchases</div>
                  </div>
                  {[
                    { p: "Apple Watch & others", a: "₦80,000" },
                    { p: "Cadbury Cake Bar", a: "₦20,000" },
                    { p: "Doritos", a: "₦9,000" },
                    { p: "Popcorn seasoning", a: "₦3,000" },
                    { p: "Magnetic Paper Clip", a: "₦1,000" },
                    { p: "Ruffles", a: "₦20,000" },
                  ].map((it, i) => (
                    <div key={i} className="flex justify-between items-center py-3 border-b border-[#12b571]/8 last:border-0">
                      <div>
                        <div className="text-sm font-semibold text-black">{it.p}</div>
                        <div className="text-xs text-black/50">#1252 · 18/02/2025</div>
                      </div>
                      <div className="font-semibold text-sm text-black">{it.a}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-[#FFF3D6] rounded-2xl px-4 py-3 shadow-lg">
                <div className="font-display font-extrabold text-2xl text-black">20</div>
                <div className="text-xs text-black/60">Total Campaigns</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="how-it-works" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFF9E6] text-black text-xs font-semibold mb-4">
              How it works
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
              From sign-up to scaling<br />in three simple steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {STEPS.map((s, i) => (
              <div key={s.num} data-testid={`step-${i}`} className="relative">
                <div className="bg-[#FFFCF2] rounded-3xl p-8 border border-[#12b571]/8 h-full card-hover">
                  <div className="font-display font-extrabold text-6xl text-black/15 mb-4">{s.num}</div>
                  <h3 className="font-display font-bold text-2xl text-black mb-3">{s.title}</h3>
                  <p className="text-black/70 leading-relaxed">{s.desc}</p>
                </div>
                {i < STEPS.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 text-black/30" size={24} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section data-testid="who-its-for" className="py-24 md:py-32 bg-[#FFF9E6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-black text-xs font-semibold mb-4">
              Who it's for
            </span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
              Built for every<br />size of ambition.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVES.map((s, i) => (
              <Link
                key={s.tag}
                to={s.href}
                data-testid={`who-its-for-${i}`}
                className={`${s.accent} rounded-3xl p-7 card-hover border border-[#12b571]/8 group block`}
              >
                <div className={`text-xs font-semibold mb-4 ${s.accent.includes("12b571") ? "text-white/70" : "text-black/60"}`}>{s.tag}</div>
                <h3 className={`font-display font-extrabold text-2xl mb-3 leading-tight ${s.accent.includes("12b571") ? "text-white" : "text-black"}`}>{s.title}</h3>
                <p className={`text-sm leading-relaxed ${s.accent.includes("12b571") ? "text-white/70" : "text-black/70"}`}>{s.desc}</p>
                <div className={`mt-6 inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all ${s.accent.includes("12b571") ? "text-white" : "text-black"}`}>
                  Explore <ChevronRight size={16} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES PREVIEW */}
      <section data-testid="modules-preview" className="py-24 md:py-32 bg-[#176842] text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#52B788]/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#FFE4E8]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold mb-4">
                Modules
              </span>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
                Pick the modules<br />you need today.<br />
                <span className="italic text-white/60 font-medium">Add more anytime.</span>
              </h2>
              <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-md">
                Brivo360 is modular. Start with what you need, then enable powerful add-ons as your business grows. No painful migrations, ever.
              </p>
              <Link to="/modules" className="mt-9 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-black font-semibold hover:bg-[#FFF9E6] transition-colors">
                Explore all modules <ArrowRight size={18} />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {MODULES.map((m, i) => (
                <div key={m.name} className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-5 hover:bg-white/15 transition-all hover:-translate-y-1">
                  <div className="w-11 h-11 rounded-xl bg-[#FFF9E6]/20 flex items-center justify-center mb-4 text-white">
                    {m.icon}
                  </div>
                  <div className="font-semibold text-white">{m.name}</div>
                  <div className="text-xs text-white/50 mt-1">Module {String(i + 1).padStart(2, "0")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* PRICING PREVIEW */}
      {/* <section data-testid="pricing-preview" className="py-24 md:py-32 bg-[#FFFCF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FFE4E8] text-black text-xs font-semibold mb-4">Pricing</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-black tracking-tight">
              Simple. Honest.<br /><span className="italic text-black/60 font-medium">Made to scale with you.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { name: "Starter", price: "$0", desc: "For solo founders", features: ["Up to 100 products", "1 staff account", "Basic POS", "Email support"], highlight: false },
              { name: "Pro", price: "$29", desc: "For growing teams", features: ["Unlimited products", "Up to 10 staff", "Multi-warehouse", "Advanced analytics", "Priority support"], highlight: true },
              { name: "Enterprise", price: "Custom", desc: "For large operations", features: ["Unlimited everything", "SSO + SLA", "Dedicated CSM", "On-prem options"], highlight: false },
            ].map((p) => (
              <div
                key={p.name}
                data-testid={`pricing-${p.name.toLowerCase()}`}
                className={`rounded-3xl p-8 border transition-all ${
                  p.highlight
                    ? "bg-[#176842] text-white border-[#176842] shadow-2xl scale-105"
                    : "bg-white border-[#176842]/8 card-hover"
                }`}
              >
                {p.highlight && (
                  <div className="inline-block px-3 py-1 rounded-full bg-[#FFF9E6] text-black text-xs font-semibold mb-4">Most popular</div>
                )}
                <div className={`font-display font-bold text-2xl ${p.highlight ? "text-white" : "text-black"}`}>{p.name}</div>
                <div className={`text-sm mt-1 ${p.highlight ? "text-white/70" : "text-black/60"}`}>{p.desc}</div>
                <div className={`mt-5 font-display font-extrabold text-5xl ${p.highlight ? "text-white" : "text-black"}`}>
                  {p.price}<span className={`text-base font-medium ${p.highlight ? "text-white/60" : "text-black/50"}`}>{p.price !== "Custom" ? "/mo" : ""}</span>
                </div>
                <ul className="mt-7 space-y-3 mb-8">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-center gap-2 text-sm ${p.highlight ? "text-white/85" : "text-black/80"}`}>
                      <Check size={16} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="https://brivopos.com/"
                  className={`block text-center w-full py-3 rounded-xl font-semibold transition-colors ${
                    p.highlight ? "bg-white text-black hover:bg-[#FFF9E6]" : "bg-[#1FA868] text-white hover:bg-[#16834D]"
                  }`}
                >
                  {p.price === "Custom" ? "Talk to sales" : "Get Started"}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section> */}

<CTASection accent="mint" />

      {/* DOWNLOAD APP */}
<section className="py-24 md:py-18 bg-[#FFFCF2]">
  <div className="max-w-7xl mx-auto px-6 lg:px-10">
    <div className="bg-[#176842] rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
      
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#52B788]/30 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#FFE4E8]/20 rounded-full blur-3xl" />

      <div className="relative">
        <h2 className="font-display text-4xl md:text-5xl font-extrabold">
          Run your business on the go
        </h2>

        <p className="mt-5 text-white/70 max-w-xl mx-auto">
          Manage your inventory, track sales, and stay in control — anytime, anywhere with the Brivo360 mobile app.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
          
          {/* App Store */}
          <a href="#" className="bg-white text-black px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#FFF9E6] transition">
            <Smartphone size={18} />
            Download on App Store
          </a>

          {/* Google Play */}
          <a href="#" className="bg-[#1FA868] text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#16834D] transition">
            <Smartphone size={18} />
            Get it on Google Play
          </a>

        </div>
      </div>
    </div>
  </div>
</section>

    

      
    </div>
  );
}
